import { ReactNode, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableRowToggleEvent, DataTableSortEvent } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import { Tag } from 'primereact/tag'
import {
  HospitalizationFilterDTO,
  HospitalizationGroupBy,
  HospitalizationGroupRowDTO,
  HospitalizationGroupsDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { SectionState } from '../../UrgencySummary/useSectionData'
import { formatCount, formatCountOrDash, formatDecimal, formatPercent, shareOf } from '../hospitalizationSummaryFormat'
import HospitalizationSectorBeds from './HospitalizationSectorBeds'

/** Las agrupaciones en el orden en que se ofrecen. Es el mismo dato visto de otra manera (SelectButton, no pestañas). */
const GROUP_BY_OPTIONS: readonly { value: HospitalizationGroupBy; messageId: string }[] = [
  { value: 'SECTOR', messageId: 'HospitalizationSummaryGroupBySector' },
  { value: 'FINANCIER', messageId: 'HospitalizationSummaryGroupByFinancier' },
  { value: 'ADMISSION_TYPE', messageId: 'HospitalizationSummaryGroupByAdmissionType' },
  { value: 'DISCHARGE_REASON', messageId: 'HospitalizationSummaryGroupByDischargeReason' },
]

/** Cómo se llama la primera columna según la agrupación. */
const NAME_HEADER_ID: Record<HospitalizationGroupBy, string> = {
  SECTOR: 'HospitalizationSummaryColSector',
  FINANCIER: 'HospitalizationSummaryColFinancier',
  ADMISSION_TYPE: 'HospitalizationSummaryColAdmissionType',
  DISCHARGE_REASON: 'HospitalizationSummaryColDischargeReason',
}

/** Cuántas filas se ven antes de «Ver todos (N)». */
export const TOP_ROWS = 10

/**
 * De qué es el «%» de cada fila: del total de la misma columna. Sector y financiador, de los días de internación; tipo de
 * ingreso, de las internaciones (ingresos); motivo de egreso, de los egresos. Se calcula acá con la fila de total.
 */
const SHARE_OF: Record<HospitalizationGroupBy, { field: 'patientDays' | 'admissions' | 'discharges'; headerId: string }> = {
  SECTOR: { field: 'patientDays', headerId: 'HospitalizationSummaryColShareDays' },
  FINANCIER: { field: 'patientDays', headerId: 'HospitalizationSummaryColShareDays' },
  ADMISSION_TYPE: { field: 'admissions', headerId: 'HospitalizationSummaryColShareAdmissions' },
  DISCHARGE_REASON: { field: 'discharges', headerId: 'HospitalizationSummaryColShareDischarges' },
}

type SortField =
  | 'name' | 'admissions' | 'discharges' | 'patientDays' | 'averageStayDays' | 'deaths' | 'occupancyPct'
  | 'share' | 'bedTurnover' | 'substitutionIntervalDays' | 'inpatientsToday'

interface SortState {
  field: SortField
  order: 1 | -1
}

/** Orden inicial de cada agrupamiento: lo que más ingresos tiene, primero (como llegan del backend). */
const DEFAULT_SORT: SortState = { field: 'admissions', order: -1 }

function compareRows(a: GroupRow, b: GroupRow, sort: SortState): number {
  if (sort.field === 'name') return sort.order * a.name.localeCompare(b.name, 'es')
  const left = a[sort.field]
  const right = b[sort.field]
  // Sin dato, siempre al final
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return sort.order * (left - right)
}

/**
 * Una fila con su clave única (`dataKey` de la tabla): el id puede ser nulo (internaciones sin financiador). `share` es su «%»
 * del total (sin redondear, para ordenar por él); las columnas solo de sector llegan siempre como número o null.
 */
type GroupRow = HospitalizationGroupRowDTO & { rowKey: string; share: number | null }

interface HospitalizationGroupedTableProps {
  groupBy: HospitalizationGroupBy
  onGroupByChange: (groupBy: HospitalizationGroupBy) => void
  /** Las filas de la agrupación elegida (consulta aparte, recién al abrir la pestaña o cambiar de agrupación). */
  groups: SectionState<HospitalizationGroupsDTO>
  /** Los filtros aplicados: las camas de un sector se piden con ellos. */
  filters: HospitalizationFilterDTO
  /** Una sola acción por fila: en financiador y tipo de ingreso, ver sus internaciones en la lista (filtra la pantalla). */
  onDrill: (groupBy: 'FINANCIER' | 'ADMISSION_TYPE', row: HospitalizationGroupRowDTO) => void
}

/**
 * Las internaciones del período agrupadas por sector, financiador, tipo de ingreso o motivo de egreso, en una sola tabla.
 * Está completa en memoria: el buscador por nombre (con más de 10 filas) y el orden por columna se resuelven acá. Se ven las
 * 10 primeras filas y «Ver todos (N)» muestra el resto. El pie suma los totales del backend. Cada fila tiene, como mucho, una acción (al final): en un sector,
 * las camas a demanda (se piden al expandirlo); en financiador y tipo de ingreso, ver sus internaciones en la lista; el
 * motivo de egreso no tiene acción. El sector de cunas se ve con su marca de texto aunque esté fuera del indicador general.
 * Una columna «%» por agrupación, calculada acá con el total (`SHARE_OF`), y, solo en sector, detrás de las demás: giro cama,
 * intervalo de sustitución e internados hoy (foto de hoy, no del período; su total es la suma de las filas).
 */
export default function HospitalizationGroupedTable({
  groupBy, onGroupByChange, groups, filters, onDrill,
}: HospitalizationGroupedTableProps) {
  const intl = useIntl()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [showAll, setShowAll] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const groupOptions = GROUP_BY_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage({ id: option.messageId }) }))

  function changeGroupBy(next: HospitalizationGroupBy) {
    setSearch('')
    setSort(DEFAULT_SORT)
    setShowAll(false)
    setExpanded({})
    onGroupByChange(next)
  }

  // Mientras llega otra consulta se sigue viendo lo anterior (atenuado), pero solo si es de la misma agrupación: las filas de
  // otra (con otras columnas y otros nombres) no se mezclan con la cabecera nueva
  const lastLoaded = useRef<{ groupBy: HospitalizationGroupBy; data: HospitalizationGroupsDTO } | null>(null)
  if (groups.data) lastLoaded.current = { groupBy, data: groups.data }
  const loaded = groups.data ?? (lastLoaded.current?.groupBy === groupBy ? lastLoaded.current.data : null)
  const loadedRows = loaded?.rows ?? null
  const totals = loaded?.totals ?? null
  // Las cunas están dentro del indicador general: su marca ya no dice «fuera del indicador»
  const nurseryIncluded = loaded?.includesNursery ?? false
  const shareField = SHARE_OF[groupBy].field
  const shareTotal = totals?.[shareField] ?? null
  const allRows = useMemo<GroupRow[]>(
    () => (loadedRows ?? []).map((row) => ({
      ...row,
      bedTurnover: row.bedTurnover ?? null,
      substitutionIntervalDays: row.substitutionIntervalDays ?? null,
      inpatientsToday: row.inpatientsToday ?? null,
      // Las cunas fuera del indicador tampoco están en el total: su parte del total no se informa
      share: row.nursery && !nurseryIncluded ? null : shareOf(row[shareField], shareTotal),
      rowKey: `${row.id ?? 'none'}-${row.name}`,
    })),
    [loadedRows, shareField, shareTotal, nurseryIncluded],
  )
  const searching = search.trim() !== ''
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('es')
    const matching = needle === '' ? allRows : allRows.filter((row) => row.name.toLocaleLowerCase('es').includes(needle))
    return [...matching].sort((a, b) => compareRows(a, b, sort))
  }, [allRows, search, sort])
  // Con una búsqueda se ven todas las que coinciden: el tope es para recorrer la lista, no para ocultar resultados
  const shownRows = searching || showAll ? rows : rows.slice(0, TOP_ROWS)
  const hasMore = !searching && rows.length > TOP_ROWS
  const showSearch = allRows.length > TOP_ROWS

  const refreshing = groups.status === 'loading'
  const centered = { className: 'nv-urg-center', headerClassName: 'nv-urg-center' }

  /** La marca de texto del sector de cunas: dice si está dentro o fuera del indicador general. */
  const nurseryTag = (row: GroupRow): ReactNode => (row.nursery ? (
    <Tag
      className="nv-tag-neutral"
      value={intl.formatMessage({ id: nurseryIncluded ? 'HospitalizationSummaryNurseryTagIn' : 'HospitalizationSummaryNurseryTagOut' })}
    />
  ) : null)

  const nameBody = (row: GroupRow): ReactNode => (
    <span className="nv-hosp-name">
      <span>{row.name}</span>
      {nurseryTag(row)}
    </span>
  )

  /** La acción de la fila, si tiene: expandir las camas del sector o ver las internaciones del financiador / tipo de ingreso. */
  const actionBody = (row: GroupRow): ReactNode => {
    if (row.id === null) return null
    if (groupBy === 'SECTOR') {
      const open = expanded[row.rowKey] === true
      return (
        // TODO(design-system): expandir una fila (las camas de un sector) no está en la lista de íconos fijos por acción; se usa el chevron del expansor.
        <Button
          type="button"
          text
          className="nv-icon nv-tip"
          icon={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
          aria-label={intl.formatMessage({ id: 'HospitalizationSummaryBedsOf' }, { name: row.name })}
          aria-expanded={open}
          data-pr-tooltip={intl.formatMessage({ id: 'HospitalizationSummaryBedsAction' })}
          onClick={() => setExpanded((current) => ({ ...current, [row.rowKey]: !open }))}
        />
      )
    }
    if (groupBy === 'FINANCIER' || groupBy === 'ADMISSION_TYPE') {
      return (
        // TODO(design-system): el ojo (ver detalle) abre acá la lista filtrada, no un detalle: no hay un ícono fijo para «ver en la lista».
        <Button
          type="button"
          text
          className="nv-icon nv-tip"
          icon="pi pi-eye"
          aria-label={intl.formatMessage({ id: 'HospitalizationSummaryViewHospitalizationsOf' }, { name: row.name })}
          data-pr-tooltip={intl.formatMessage({ id: 'HospitalizationSummaryViewHospitalizations' })}
          onClick={() => onDrill(groupBy, row)}
        />
      )
    }
    return null
  }

  const hasAction = groupBy !== 'DISCHARGE_REASON'
  const actionHeader = (
    <span className="nv-sr-only">
      {intl.formatMessage({ id: groupBy === 'SECTOR' ? 'HospitalizationSummaryBedsAction' : 'HospitalizationSummaryViewHospitalizations' })}
    </span>
  )

  /** El «%» de la fila va pegado a la columna de la que se calcula; la fila de total es el 100 %. */
  const shareColumn = (field: typeof shareField): ReactNode => (shareField === field ? (
    <Column
      field="share"
      header={intl.formatMessage({ id: SHARE_OF[groupBy].headerId })}
      sortable
      {...centered}
      footer={totals ? formatPercent(intl, shareOf(shareTotal, shareTotal)) : undefined}
      body={(row: GroupRow) => formatPercent(intl, row.share)}
    />
  ) : null)

  const expansion = (row: GroupRow): ReactNode =>
    (row.id === null ? null : <HospitalizationSectorBeds filters={filters} sectorId={row.id} sectorName={row.name} />)

  return (
    <div className="nv-hosp-group">
      <div className="nv-hosp-toolbar">
        <SelectButton
          value={groupBy}
          options={groupOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) changeGroupBy(event.value as HospitalizationGroupBy) }}
          aria-label={intl.formatMessage({ id: 'HospitalizationSummaryGroupBy' })}
        />
        {showSearch && (
          <span className="p-input-icon-left">
            <i className="pi pi-search" aria-hidden="true" />
            <InputText
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={intl.formatMessage({ id: 'HospitalizationSummaryGroupSearch' })}
              aria-label={intl.formatMessage({ id: 'HospitalizationSummaryGroupSearch' })}
            />
          </span>
        )}
      </div>

      <div className="nv-hosp-group__body" aria-busy={refreshing}>
        {refreshing && allRows.length === 0 && (
          <span className="nv-loading" role="status">
            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
            <span>{intl.formatMessage({ id: 'HospitalizationSummaryLoading' })}</span>
          </span>
        )}
        {groups.status === 'failed' && (
          <div className="nv-hosp-failed" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {intl.formatMessage({ id: 'HospitalizationSummaryGroupsError' })}
            </p>
            <Button type="button" link label={intl.formatMessage({ id: 'HospitalizationSummaryRetry' })} onClick={groups.retry} />
          </div>
        )}
        {groups.status !== 'failed' && !(refreshing && allRows.length === 0) && (allRows.length === 0
          ? <p className="nv-urg-empty">{intl.formatMessage({ id: 'HospitalizationSummaryEmpty' })}</p>
          : (
            <div className={`nv-load-table${refreshing ? ' nv-hosp-blurred' : ''}`}>
              <DataTable
                // Al cambiar de agrupamiento las columnas son otras
                key={groupBy}
                value={shownRows}
                dataKey="rowKey"
                lazy
                sortField={sort.field}
                sortOrder={sort.order}
                onSort={(event: DataTableSortEvent) => setSort({ field: event.sortField as SortField, order: event.sortOrder === 1 ? 1 : -1 })}
                emptyMessage={intl.formatMessage({ id: 'HospitalizationSummaryGroupNoMatches' })}
                expandedRows={groupBy === 'SECTOR' ? expanded : undefined}
                onRowToggle={(event: DataTableRowToggleEvent) => setExpanded(event.data as Record<string, boolean>)}
                rowExpansionTemplate={groupBy === 'SECTOR' ? expansion : undefined}
              >
                <Column
                  field="name"
                  header={intl.formatMessage({ id: NAME_HEADER_ID[groupBy] })}
                  sortable
                  body={nameBody}
                  footer={totals ? intl.formatMessage({ id: 'HospitalizationSummaryTotal' }) : undefined}
                />
                <Column
                  field="admissions"
                  header={intl.formatMessage({ id: 'HospitalizationSummaryKpiAdmissions' })}
                  footer={totals ? formatCount(intl, totals.admissions) : undefined}
                  sortable
                  {...centered}
                  body={(row: GroupRow) => formatCount(intl, row.admissions)}
                />
                {shareColumn('admissions')}
                <Column
                  field="discharges"
                  header={intl.formatMessage({ id: 'HospitalizationSummaryKpiDischarges' })}
                  sortable
                  {...centered}
                  footer={totals ? formatCount(intl, totals.discharges) : undefined}
                  body={(row: GroupRow) => formatCount(intl, row.discharges)}
                />
                {shareColumn('discharges')}
                <Column
                  field="patientDays"
                  header={intl.formatMessage({ id: 'HospitalizationSummaryColPatientDays' })}
                  sortable
                  {...centered}
                  footer={totals ? formatCount(intl, totals.patientDays) : undefined}
                  body={(row: GroupRow) => formatCount(intl, row.patientDays)}
                />
                {shareColumn('patientDays')}
                <Column
                  field="averageStayDays"
                  header={intl.formatMessage({ id: 'HospitalizationSummaryKpiAverageStay' })}
                  sortable
                  {...centered}
                  footer={totals ? formatDecimal(intl, totals.averageStayDays) : undefined}
                  body={(row: GroupRow) => formatDecimal(intl, row.averageStayDays)}
                />
                <Column
                  field="deaths"
                  header={intl.formatMessage({ id: 'HospitalizationSummaryColDeaths' })}
                  sortable
                  {...centered}
                  footer={totals ? formatCount(intl, totals.deaths) : undefined}
                  body={(row: GroupRow) => formatCount(intl, row.deaths)}
                />
                {groupBy === 'SECTOR' && (
                  <Column
                    field="occupancyPct"
                    header={intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancy' })}
                    sortable
                    {...centered}
                    footer={totals ? formatPercent(intl, totals.occupancyPct) : undefined}
                    body={(row: GroupRow) => formatPercent(intl, row.occupancyPct)}
                  />
                )}
                {/* Detrás de las existentes: la tabla se desplaza dentro de su caja si no entran */}
                {groupBy === 'SECTOR' && (
                  <Column
                    field="bedTurnover"
                    header={intl.formatMessage({ id: 'HospitalizationSummaryKpiBedTurnover' })}
                    sortable
                    {...centered}
                    footer={totals ? formatDecimal(intl, totals.bedTurnover) : undefined}
                    body={(row: GroupRow) => formatDecimal(intl, row.bedTurnover)}
                  />
                )}
                {groupBy === 'SECTOR' && (
                  <Column
                    field="substitutionIntervalDays"
                    header={intl.formatMessage({ id: 'HospitalizationSummaryKpiSubstitution' })}
                    sortable
                    {...centered}
                    footer={totals ? formatDecimal(intl, totals.substitutionIntervalDays) : undefined}
                    body={(row: GroupRow) => formatDecimal(intl, row.substitutionIntervalDays)}
                  />
                )}
                {groupBy === 'SECTOR' && (
                  <Column
                    field="inpatientsToday"
                    header={intl.formatMessage({ id: 'HospitalizationSummaryColInpatientsToday' })}
                    sortable
                    {...centered}
                    footer={totals ? formatCountOrDash(intl, totals.inpatientsToday) : undefined}
                    body={(row: GroupRow) => formatCountOrDash(intl, row.inpatientsToday)}
                  />
                )}
                {hasAction && (
                  <Column header={actionHeader} className="nv-col-actions" headerClassName="nv-col-actions" body={actionBody} />
                )}
              </DataTable>
            </div>
          ))}
      </div>

      {hasMore && (
        <div className="nv-hosp-more">
          <Button
            type="button"
            link
            label={intl.formatMessage(
              { id: showAll ? 'HospitalizationSummaryShowLess' : 'HospitalizationSummaryShowAll' },
              { count: rows.length, countText: formatCount(intl, rows.length), top: TOP_ROWS },
            )}
            aria-expanded={showAll}
            onClick={() => setShowAll((value) => !value)}
          />
        </div>
      )}
    </div>
  )
}
